import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { api, throwIfNotOk } from '../lib/api';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { OfferStatusBadge } from '@siliconx/shared/ui/OfferStatusBadge';
import { formatDate } from '../lib/format';
import { isFutureDate, formatMonthYear, formatDayMonthYear } from '../lib/dates';
import { useAuth } from '../lib/authContext';
import { exchangeErrorMessage } from '../components/exchange/errors';
import type { Offer } from '@siliconx/shared/dto/offer';
import {
  ESCROW_DEPOSIT_FRACTION,
  type EscrowBalanceStatus,
  type EscrowDepositView,
  type PayDepositResponse,
} from '@siliconx/shared/dto/offer-payment';
import type { OfferAmendment } from '@siliconx/shared/dto/offer-amendment';
import type { PaymentMethodDetail } from '@siliconx/shared/dto/payment-method';
import type { OffersResponse } from '../lib/types';
import { qk } from '../lib/queryKeys';
import { Card } from '@siliconx/shared/ui/Card';
import { SafeLink } from '@siliconx/shared/ui/SafeLink';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { MyActivity } from '../components/exchange/activity/MyActivity';
import {
  createPurchaseTermsConsent,
  isPurchaseTermsConsentRequiredError,
} from '../lib/purchaseTerms';
import {
  PURCHASE_TERMS_BODY,
  PURCHASE_TERMS_CHECKBOX_LABEL,
  PURCHASE_TERMS_CONSENT_ERROR,
  PURCHASE_TERMS_HEADING,
} from '../components/exchange/copy/purchaseTerms';

export const Route = createFileRoute('/offers')({
  component: OffersPage,
});

// ---------------------------------------------------------------------------
// 669: binding forward-commitment confirmation. Shown instead of a one-click
// accept whenever the offer's listing has a FUTURE availableOn — accepting
// (plus the deposit) commits both parties until the settlement date.
// ---------------------------------------------------------------------------
function ForwardCommitDialog({
  availableOn,
  onConfirm,
  onCancel,
  isPending,
}: {
  availableOn: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="max-w-sm shadow-lg w-full">
        <h2 className="text-base font-semibold text-zinc-900">Commit to a forward sale?</h2>
        <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
          <p>
            <span className="font-semibold">
              Settlement date: {formatDayMonthYear(availableOn)}.
            </span>{' '}
            This deal is binding — the deposit is held in escrow until settlement and there is
            no self-serve cancellation. If the seller cannot deliver, the buyer&rsquo;s deposit
            is refunded.
          </p>
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="text-sm text-zinc-500 hover:text-zinc-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {isPending ? 'Accepting…' : `Accept — settles ${formatMonthYear(availableOn)}`}
          </button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seller — offers on my listings: accept / reject / counter a pending offer.
// Once countered (RUF-568, single round), the seller has no further action —
// the buyer must accept the counter or decline.
// ---------------------------------------------------------------------------
function SellerOfferActions({ offer }: { offer: Offer }) {
  const queryClient = useQueryClient();
  const [countering, setCountering] = useState(false);
  const [counterAmount, setCounterAmount] = useState('');
  const [confirmingForward, setConfirmingForward] = useState(false);
  const forwardDated = isFutureDate(offer.listingAvailableOn);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.offers() });

  const accept = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].accept.$post({ param: { id: offer.id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Offer accepted.');
      setConfirmingForward(false);
      void invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Accept failed.'),
  });

  const reject = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].reject.$post({ param: { id: offer.id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Offer rejected.');
      void invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Reject failed.'),
  });

  const counter = useMutation({
    mutationFn: async (counterAmountUsd: number) => {
      const res = await api.api.client.offers[':id'].counter.$post({
        param: { id: offer.id },
        json: { counterAmountUsd },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Counter sent.');
      setCountering(false);
      setCounterAmount('');
      void invalidate();
    },
    // SIL-1427: BUY_NOW_NOT_COUNTERABLE should be unreachable here — the
    // Counter button is hidden for a kind='buy_now' row below — but mapped
    // anyway as a backstop rather than letting a raw 409 surface.
    onError: (err: Error) => toast.error(exchangeErrorMessage(err, 'Counter failed.')),
  });

  // SIL-645: once accepted (or deposit-held), the seller's action is amend/cancel.
  // SIL-1598: with the deposit secured on the Escrow.com rail the seller also requests the balance.
  if (offer.status === 'accepted' || offer.status === 'deposit_held') {
    return (
      <div className="flex flex-col items-start gap-2">
        {offer.status === 'deposit_held' && <SellerBalanceAction offer={offer} />}
        <AmendOfferAction offer={offer} />
      </div>
    );
  }

  if (offer.status !== 'pending') return null;

  if (countering) {
    const amount = Number(counterAmount);
    const valid = counterAmount.trim() !== '' && amount > 0;
    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0.01"
          step="0.01"
          autoFocus
          value={counterAmount}
          onChange={(e) => setCounterAmount(e.target.value)}
          placeholder="Amount"
          className="w-24 rounded border border-zinc-300 px-2 py-1 text-xs"
        />
        <button
          type="button"
          onClick={() => valid && counter.mutate(amount)}
          disabled={!valid || counter.isPending}
          className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
        >
          {counter.isPending ? '…' : 'Send'}
        </button>
        <button
          type="button"
          onClick={() => {
            setCountering(false);
            setCounterAmount('');
          }}
          className="rounded border border-zinc-300 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      {confirmingForward && offer.listingAvailableOn && (
        <ForwardCommitDialog
          availableOn={offer.listingAvailableOn}
          onConfirm={() => accept.mutate()}
          onCancel={() => setConfirmingForward(false)}
          isPending={accept.isPending}
        />
      )}
      <button
        type="button"
        onClick={() => (forwardDated ? setConfirmingForward(true) : accept.mutate())}
        disabled={accept.isPending}
        className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
      >
        {accept.isPending ? '…' : offer.kind === 'buy_now' ? 'Confirm availability' : 'Accept'}
      </button>
      <button
        type="button"
        onClick={() => reject.mutate()}
        disabled={reject.isPending}
        className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
      >
        {reject.isPending ? '…' : offer.kind === 'buy_now' ? 'Decline' : 'Reject'}
      </button>
      {/* SIL-1427: a buy_now offer can never be countered (migration 972's
          tg_offer_status_transition rejects it) — the control is hidden
          rather than shown and mapped to a 409 on click. */}
      {offer.kind !== 'buy_now' && (
        <button
          type="button"
          onClick={() => setCountering(true)}
          className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
        >
          Counter
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SIL-1598: the SELLER requests the balance (agreed price - deposit) once the deposit is secured on the
// Escrow.com rail. The status comes from a seller-scoped DB function (no pay link, no buyer email); a
// non-escrow offer answers `balance: null` and nothing renders. Button visibility is UX only: the DB
// (request_escrow_balance) is the boundary, and the API maps its refusals to fixed 409 codes.
// ---------------------------------------------------------------------------
function SellerBalanceAction({ offer }: { offer: Offer }) {
  const queryClient = useQueryClient();
  const key = [...qk.offers(), offer.id, 'escrow-balance'];
  const { data, isLoading } = useQuery<{ balance: EscrowBalanceStatus | null }>({
    queryKey: key,
    queryFn: async () => {
      const res = await api.api.client.offers[':id'].escrow.balance.$get({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { balance: EscrowBalanceStatus | null };
    },
    refetchInterval: (q) => {
      const st = q.state.data?.balance?.status;
      return st === 'pending' || st === 'link_sent' ? 15_000 : false;
    },
  });

  const request = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].escrow['request-balance'].$post({
        param: { id: offer.id },
        json: {},
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Balance requested. The buyer will be asked to fund it on Escrow.com.');
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not request the balance.'),
  });

  if (isLoading) return null;
  const balance = data?.balance;
  if (!balance) return null;

  return (
    <div className="flex flex-col gap-1 rounded border border-teal-300 bg-teal-50 p-2 text-xs" data-testid="escrow-balance-seller">
      {balance.status === 'none' && (
        <>
          <span className="text-zinc-700">The deposit is secured. Request the balance from the buyer when you are ready.</span>
          <button
            type="button"
            onClick={() => request.mutate()}
            disabled={request.isPending}
            className="w-fit rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
          >
            {request.isPending ? '…' : 'Request balance'}
          </button>
        </>
      )}
      {balance.status === 'pending' && (
        <span className="text-zinc-700">
          Balance requested{balance.amountUsd != null && <> (<UsdCell value={balance.amountUsd} />)</>}. Creating the Escrow.com
          transaction. This page updates automatically.
        </span>
      )}
      {balance.status === 'link_sent' && (
        <span className="text-zinc-700">
          Waiting for the buyer to fund the balance{balance.amountUsd != null && <> (<UsdCell value={balance.amountUsd} />)</>} on Escrow.com.
        </span>
      )}
      {balance.status === 'funded' && (
        <span className="font-medium text-teal-800">
          Balance funded{balance.amountUsd != null && <> (<UsdCell value={balance.amountUsd} />)</>}. Held by Escrow.com until settlement.
        </span>
      )}
    </div>
  );
}

// SIL-1598: the buyer's balance pay link. Same flow, API and consent gate as the deposit: the live balance
// comes back from GET /:id/escrow once one exists (kind 'balance'); until then (deposit only) nothing renders.
function BalanceAction({ offer }: { offer: Offer }) {
  const { data, isLoading, refetch, isFetching } = useQuery<{ escrow: EscrowDepositView | null }>({
    queryKey: [...qk.offers(), offer.id, 'escrow'],
    queryFn: async () => {
      const res = await api.api.client.offers[':id'].escrow.$get({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { escrow: EscrowDepositView | null };
    },
    gcTime: 0,
    refetchInterval: (q) => (q.state.data?.escrow?.status === 'pending_create' ? 15_000 : false),
  });
  if (isLoading) return null;
  const escrow = data?.escrow;
  if (!escrow || escrow.kind !== 'balance') return null;
  return (
    <div className="flex flex-col gap-2 rounded border border-teal-300 bg-teal-50 p-2 text-xs" data-testid="escrow-balance">
      <div>
        Balance due: <UsdCell value={escrow.amountUsd} /> (the agreed price of <UsdCell value={offer.amountUsd} /> minus your
        deposit), held by Escrow.com
      </div>
      {escrow.error === 'consent_required' ? (
        <EscrowConsentStep offerId={offer.id} onRecorded={() => void refetch()} />
      ) : escrow.payUrl ? (
        <SafeLink
          href={escrow.payUrl}
          fallback="Payment link unavailable"
          className="w-fit rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100"
        >
          Pay balance on Escrow.com
        </SafeLink>
      ) : escrow.error ? (
        <div className="flex items-center gap-2 text-zinc-700">
          <span>We could not load your Escrow.com payment link.</span>
          <button
            type="button"
            disabled={isFetching}
            onClick={() => void refetch()}
            className="rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100"
          >
            Try again
          </button>
        </div>
      ) : escrow.status === 'open' ? (
        <span className="text-zinc-600">Your payment link is ready.</span>
      ) : (
        <span className="text-zinc-600">Preparing your Escrow.com payment link. This page updates automatically.</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SIL-1593: business-org offers pay their 20% deposit on Escrow.com. When the
// offer has a live Escrow.com deposit transaction, show its pay link INSTEAD
// of the mock deposit flow; otherwise fall through to the mock flow below.
// The link only ever comes from the API (https escrow.com host, buyer org only).
// ---------------------------------------------------------------------------
function DepositAction({ offer }: { offer: Offer }) {
  const { data, isLoading, refetch, isFetching } = useQuery<{ escrow: EscrowDepositView | null }>({
    queryKey: [...qk.offers(), offer.id, 'escrow'],
    queryFn: async () => {
      const res = await api.api.client.offers[':id'].escrow.$get({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { escrow: EscrowDepositView | null };
    },
    // The pay link is a buyer credential: never keep it in the cache beyond this view
    // (gcTime 0), never persist it. Poll only while the transaction is still being
    // created; after an Escrow.com error stop and offer a manual retry.
    gcTime: 0,
    refetchInterval: (q) => (q.state.data?.escrow?.status === 'pending_create' ? 15_000 : false),
  });
  if (isLoading) return null;
  const escrow = data?.escrow;
  if (!escrow) return <PayDepositAction offer={offer} />;
  return (
    <div className="flex flex-col gap-2 rounded border border-teal-300 bg-teal-50 p-2 text-xs" data-testid="escrow-deposit">
      <div>
        Deposit: <UsdCell value={escrow.amountUsd} /> (20% of <UsdCell value={offer.amountUsd} />), held by
        Escrow.com
      </div>
      {escrow.error === 'consent_required' ? (
        <EscrowConsentStep offerId={offer.id} onRecorded={() => void refetch()} />
      ) : escrow.payUrl ? (
        <SafeLink
          href={escrow.payUrl}
          fallback="Payment link unavailable"
          className="w-fit rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100"
        >
          Pay deposit on Escrow.com
        </SafeLink>
      ) : escrow.error ? (
        <div className="flex items-center gap-2 text-zinc-700">
          <span>We could not load your Escrow.com payment link.</span>
          <button
            type="button"
            disabled={isFetching}
            onClick={() => void refetch()}
            className="rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100"
          >
            Try again
          </button>
        </div>
      ) : (
        <span className="text-zinc-600">Preparing your Escrow.com payment link. This page updates automatically.</span>
      )}
    </div>
  );
}

// SIL-1594: the Escrow.com pay link is only returned once the buyer has recorded the
// same offer_deposit purchase-terms consent the mock deposit requires (SIL-1364). Until
// then the API answers error 'consent_required' with no link; this step records the
// consent (append-only) and refetches, which then returns the link.
function EscrowConsentStep({ offerId, onRecorded }: { offerId: string; onRecorded: () => void }) {
  const [agreed, setAgreed] = useState(false);
  const record = useMutation({
    mutationFn: async () => {
      const consent = await createPurchaseTermsConsent({ bindingEvent: 'offer_deposit', offerId });
      if (!consent.consentId) throw new Error('Purchase terms consent was not recorded.');
    },
    onSuccess: onRecorded,
    onError: (err: Error) => toast.error(err.message ?? 'Could not record consent.'),
  });
  return (
    <div className="rounded border border-teal-200 bg-white p-2" data-testid="escrow-consent">
      <p className="font-medium text-zinc-900">{PURCHASE_TERMS_HEADING}</p>
      <p className="mt-1 text-zinc-600">{PURCHASE_TERMS_BODY}</p>
      <label className="mt-2 flex items-start gap-2">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5"
        />
        <span>{PURCHASE_TERMS_CHECKBOX_LABEL}</span>
      </label>
      <button
        type="button"
        disabled={!agreed || record.isPending}
        onClick={() => record.mutate()}
        className="mt-2 rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
      >
        {record.isPending ? '…' : 'Agree and continue to payment'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Buyer — pay the mock escrow deposit on an accepted offer (RUF-570). Shows
// the 20% deposit amount + the org's default payment method before
// confirming. Mock auto-succeeds — no fake pending state.
//
// SIL-1424: a purchase-terms consent step gates the deposit call. The
// "Confirm & pay" action is disabled until the accept checkbox is checked,
// and its mutation POSTs the consent FIRST — the deposit call
// (`offers/:id/pay-deposit`) is never reached without a `consentId` back
// from that POST. A 966 enforcement rejection (PURCHASE_TERMS_CONSENT_REQUIRED,
// mapped by the API to `purchase_terms_consent_required`) resets the
// checkbox and shows an inline message on this step rather than a generic
// toast — see `isPurchaseTermsConsentRequiredError`.
// ---------------------------------------------------------------------------
function PayDepositAction({ offer }: { offer: Offer }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.offers() });

  const { data: methods } = useQuery<{ data: PaymentMethodDetail[] }>({
    queryKey: qk.paymentMethods(),
    queryFn: async () => {
      const res = await api.api.client.me['payment-methods'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { data: PaymentMethodDetail[] };
    },
    enabled: confirming,
  });
  const defaultMethod = methods?.data.find((m) => m.isDefault) ?? null;
  const depositAmount = Math.round(offer.amountUsd * ESCROW_DEPOSIT_FRACTION * 100) / 100;
  const balanceAmount = Math.round((offer.amountUsd - depositAmount) * 100) / 100;

  const payDeposit = useMutation({
    mutationFn: async () => {
      // Consent FIRST. Its `consentId` gates the deposit call below — there
      // is no path from this mutation to `pay-deposit` without one.
      const consent = await createPurchaseTermsConsent({
        bindingEvent: 'offer_deposit',
        offerId: offer.id,
      });
      if (!consent.consentId) {
        throw new Error('Purchase terms consent was not recorded.');
      }
      const res = await api.api.client.offers[':id']['pay-deposit'].$post({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as PayDepositResponse;
    },
    onSuccess: () => {
      toast.success('Deposit paid — held in escrow.');
      setConfirming(false);
      setAgreedToTerms(false);
      setTermsError(null);
      void invalidate();
    },
    onError: (err: Error) => {
      // 966's PT001 rejection lands here, not in a generic toast — send the
      // buyer back to the terms step rather than an opaque error.
      if (isPurchaseTermsConsentRequiredError(err)) {
        setAgreedToTerms(false);
        setTermsError(PURCHASE_TERMS_CONSENT_ERROR);
        return;
      }
      toast.error(err.message ?? 'Pay deposit failed.');
    },
  });

  if (offer.status !== 'accepted') return null;

  if (confirming) {
    return (
      <div className="flex flex-col gap-2 rounded border border-teal-300 bg-teal-50 p-2 text-xs">
        <div>
          Deposit: <UsdCell value={depositAmount} /> (20% of <UsdCell value={offer.amountUsd} />)
        </div>
        <div>
          Balance: <UsdCell value={balanceAmount} />
        </div>
        <div>
          Method:{' '}
          {defaultMethod ? defaultMethod.maskedIdentifier : 'No payment method on file'}
        </div>
        <div className="rounded border border-teal-200 bg-white p-2">
          <p className="font-medium text-zinc-900">{PURCHASE_TERMS_HEADING}</p>
          <p className="mt-1 text-zinc-600">{PURCHASE_TERMS_BODY}</p>
          <label className="mt-2 flex items-start gap-2">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e.target.checked);
                setTermsError(null);
              }}
              className="mt-0.5"
            />
            <span>{PURCHASE_TERMS_CHECKBOX_LABEL}</span>
          </label>
          {termsError && <p className="mt-1 text-red-600">{termsError}</p>}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => payDeposit.mutate()}
            disabled={payDeposit.isPending || !defaultMethod || !agreedToTerms}
            className="rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
          >
            {payDeposit.isPending ? '…' : 'Confirm & pay (mock)'}
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirming(false);
              setAgreedToTerms(false);
              setTermsError(null);
            }}
            className="rounded border border-zinc-300 px-2 py-1 font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className="rounded border border-teal-300 px-2 py-1 text-xs font-medium text-teal-700 hover:bg-teal-50"
    >
      Pay deposit (mock)
    </button>
  );
}

// ---------------------------------------------------------------------------
// SIL-645: amend an accepted offer — either party may propose a revised
// price on an accepted/deposit_held offer; it takes effect only when the
// COUNTERPARTY explicitly accepts. Full append-only revision history is
// shown. Settlement of an accepted amendment's escrow delta (top-up /
// partial release) is a SEPARATE deliberate action, never chained onto
// acceptance. Cancelling the deal outright is also here as a distinct
// explicit transition with a required reason — not a rollback.
// ---------------------------------------------------------------------------
interface AmendmentHistoryResponse {
  data: OfferAmendment[];
}

/** The still-open proposal, if any: a 'proposed' row with no row referencing it via proposalId. */
function findOpenProposal(history: OfferAmendment[]): OfferAmendment | null {
  const proposals = history.filter((a) => a.kind === 'proposed');
  const resolvedIds = new Set(history.filter((a) => a.proposalId != null).map((a) => a.proposalId));
  return proposals.find((p) => !resolvedIds.has(p.id)) ?? null;
}

function AmendmentDialog({ offer, onClose }: { offer: Offer; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [proposing, setProposing] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const historyKey = qk.offerAmendments(offer.id);
  const invalidateHistory = () => queryClient.invalidateQueries({ queryKey: historyKey });
  const invalidateOffers = () => queryClient.invalidateQueries({ queryKey: qk.offers() });

  const { data: history, isFetching } = useQuery<AmendmentHistoryResponse>({
    queryKey: historyKey,
    queryFn: async () => {
      const res = await api.api.client.offers[':id'].amendments.$get({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as AmendmentHistoryResponse;
    },
  });

  // SIL-1598: on the Escrow.com rail the balance absorbs a price change; the mock top-up is refused by the DB
  // (escrow_managed), so the control is hidden. Only the buyer can read the escrow view (404-free: null when none).
  const { data: escrowView } = useQuery<{ escrow: EscrowDepositView | null }>({
    queryKey: [...qk.offers(), offer.id, 'escrow'],
    enabled: offer.isOwnOffer,
    queryFn: async () => {
      const res = await api.api.client.offers[':id'].escrow.$get({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { escrow: EscrowDepositView | null };
    },
    gcTime: 0,
  });
  const escrowManaged = !!escrowView?.escrow;

  const rows = history?.data ?? [];
  const openProposal = findOpenProposal(rows);
  const settlementRows = rows.filter(
    (a) => a.kind === 'accepted' && a.escrowDeltaUsd != null && a.escrowDeltaUsd !== 0 && !a.settled,
  );

  const propose = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].amendments.$post({
        param: { id: offer.id },
        json: { amountUsd: Number(amount), reason },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Amendment proposed — waiting on the counterparty.');
      setProposing(false);
      setAmount('');
      setReason('');
      void invalidateHistory();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Propose failed.'),
  });

  const accept = useMutation({
    mutationFn: async (amendmentId: string) => {
      const res = await api.api.client.offers[':id'].amendments[':amendmentId'].accept.$post({
        param: { id: offer.id, amendmentId },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Amendment accepted — the offer price is updated.');
      void invalidateHistory();
      void invalidateOffers();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Accept failed.'),
  });

  const decline = useMutation({
    mutationFn: async (amendmentId: string) => {
      const res = await api.api.client.offers[':id'].amendments[':amendmentId'].decline.$post({
        param: { id: offer.id, amendmentId },
        json: {},
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Amendment declined.');
      void invalidateHistory();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Decline failed.'),
  });

  const payTopup = useMutation({
    mutationFn: async (amendmentId: string) => {
      const res = await api.api.client.offers[':id'].amendments[':amendmentId']['pay-topup'].$post({
        param: { id: offer.id, amendmentId },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Top-up paid — held in escrow.');
      void invalidateHistory();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Pay top-up failed.'),
  });

  const cancelOffer = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].cancel.$post({
        param: { id: offer.id },
        json: { reason: cancelReason },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Offer cancelled.');
      onClose();
      void invalidateOffers();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Cancel failed.'),
  });

  const canPropose = offer.status === 'accepted' || offer.status === 'deposit_held';
  const amountNum = Number(amount);
  const proposeValid = amount.trim() !== '' && amountNum > 0 && reason.trim() !== '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="max-h-[85vh] w-full max-w-lg overflow-y-auto shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-zinc-900">Amend offer</h2>
          <button type="button" onClick={onClose} className="text-sm text-zinc-400 hover:text-zinc-600">
            Close
          </button>
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          Current price: <UsdCell value={offer.amountUsd} /> · either party may propose a revised
          price; it takes effect only once the other side accepts.
        </p>

        {/* Revision history — the full append-only trail, both parties. */}
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">History</h3>
          {isFetching && rows.length === 0 && (
            <p className="mt-1 text-xs text-zinc-400">Loading…</p>
          )}
          {!isFetching && rows.length === 0 && (
            <p className="mt-1 text-xs text-zinc-400">No amendments proposed yet.</p>
          )}
          <ul className="mt-2 space-y-2">
            {rows.map((a) => (
              <li key={a.id} className="rounded border border-zinc-200 p-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-zinc-800">
                    {a.isOwnAction ? 'You' : 'Counterparty'}{' '}
                    {a.kind === 'proposed' ? 'proposed' : a.kind === 'accepted' ? 'accepted' : 'declined'}{' '}
                    <UsdCell value={a.amountUsd} />
                  </span>
                  <span className="text-zinc-400">{formatDate(a.createdAt)}</span>
                </div>
                <p className="mt-0.5 italic text-zinc-500">&ldquo;{a.reason}&rdquo;</p>
                {a.kind === 'accepted' && a.escrowDeltaUsd != null && a.escrowDeltaUsd !== 0 && (
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className={a.escrowDeltaUsd > 0 ? 'text-amber-700' : 'text-blue-700'}>
                      {a.escrowDeltaUsd > 0
                        ? `Top-up owed: `
                        : `Partial release owed: `}
                      <UsdCell value={Math.abs(a.escrowDeltaUsd)} />
                      {a.settled ? ' — settled' : ' — not yet settled'}
                    </span>
                    {!a.settled && a.escrowDeltaUsd > 0 && offer.isOwnOffer && escrowManaged && (
                      <span className="text-zinc-500">The balance on Escrow.com covers this change</span>
                    )}
                    {!a.settled && a.escrowDeltaUsd > 0 && offer.isOwnOffer && !escrowManaged && (
                      <button
                        type="button"
                        onClick={() => payTopup.mutate(a.id)}
                        disabled={payTopup.isPending}
                        className="rounded border border-amber-300 px-2 py-1 font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                      >
                        {payTopup.isPending ? '…' : 'Pay top-up (mock)'}
                      </button>
                    )}
                    {!a.settled && a.escrowDeltaUsd < 0 && (
                      <span className="text-zinc-400">
                        {escrowManaged ? 'The balance on Escrow.com covers this change' : 'Awaiting SiliconX ops release'}
                      </span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Open proposal — counterparty may accept/decline; the proposer waits. */}
        {openProposal && (
          <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
            {openProposal.isOwnAction ? (
              <p className="text-blue-900">
                Your proposal of <UsdCell value={openProposal.amountUsd} /> is pending the
                counterparty&rsquo;s response.
              </p>
            ) : (
              <>
                <p className="text-blue-900">
                  The counterparty proposed <UsdCell value={openProposal.amountUsd} /> —
                  &ldquo;{openProposal.reason}&rdquo;
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => accept.mutate(openProposal.id)}
                    disabled={accept.isPending}
                    className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
                  >
                    {accept.isPending ? '…' : 'Accept'}
                  </button>
                  <button
                    type="button"
                    onClick={() => decline.mutate(openProposal.id)}
                    disabled={decline.isPending}
                    className="rounded border border-zinc-300 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    {decline.isPending ? '…' : 'Decline'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Propose a new amendment — only when nothing is currently open. */}
        {canPropose && !openProposal && (
          <div className="mt-4">
            {proposing ? (
              <div className="flex flex-col gap-2 rounded border border-zinc-200 p-2 text-xs">
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  autoFocus
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="New amount (USD)"
                  className="rounded border border-zinc-300 px-2 py-1"
                />
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (e.g. inspection found a fault)"
                  rows={2}
                  className="rounded border border-zinc-300 px-2 py-1"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => proposeValid && propose.mutate()}
                    disabled={!proposeValid || propose.isPending}
                    className="rounded border border-blue-300 px-2 py-1 font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    {propose.isPending ? '…' : 'Send proposal'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setProposing(false);
                      setAmount('');
                      setReason('');
                    }}
                    className="rounded border border-zinc-300 px-2 py-1 font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setProposing(true)}
                className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
              >
                Propose an amendment
              </button>
            )}
          </div>
        )}

        {/* Cancel the deal outright — explicit transition, required reason, not a rollback. */}
        {canPropose && (
          <div className="mt-6 border-t border-zinc-100 pt-3">
            {cancelling ? (
              <div className="flex flex-col gap-2 rounded border border-red-200 bg-red-50 p-2 text-xs">
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Reason for cancelling this deal"
                  rows={2}
                  className="rounded border border-red-300 px-2 py-1"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => cancelReason.trim() !== '' && cancelOffer.mutate()}
                    disabled={cancelReason.trim() === '' || cancelOffer.isPending}
                    className="rounded border border-red-400 px-2 py-1 font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    {cancelOffer.isPending ? '…' : 'Confirm cancellation'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCancelling(false);
                      setCancelReason('');
                    }}
                    className="rounded border border-zinc-300 px-2 py-1 font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Keep the deal
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCancelling(true)}
                className="text-xs font-medium text-red-600 hover:underline"
              >
                Cancel this deal
              </button>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

/** Trigger button for either party — opens AmendmentDialog. */
function AmendOfferAction({ offer }: { offer: Offer }) {
  const [open, setOpen] = useState(false);
  if (offer.status !== 'accepted' && offer.status !== 'deposit_held') return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded border border-zinc-300 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
      >
        Amend / cancel
      </button>
      {open && <AmendmentDialog offer={offer} onClose={() => setOpen(false)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Buyer — my offers: withdraw a pending offer, accept/decline a seller's
// counter (RUF-568), or pay the mock escrow deposit on an accepted offer
// (RUF-570). Declining a counter is a plain withdraw (no new status).
// ---------------------------------------------------------------------------
function BuyerOfferActions({ offer }: { offer: Offer }) {
  const queryClient = useQueryClient();
  const [confirmingForward, setConfirmingForward] = useState(false);
  const forwardDated = isFutureDate(offer.listingAvailableOn);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.offers() });

  const withdraw = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].withdraw.$post({ param: { id: offer.id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success(offer.status === 'countered' ? 'Counter declined.' : 'Offer withdrawn.');
      void invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Action failed.'),
  });

  const acceptCounter = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id']['accept-counter'].$post({ param: { id: offer.id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Counter accepted.');
      setConfirmingForward(false);
      void invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Accept counter failed.'),
  });

  if (offer.status === 'countered') {
    return (
      <div className="flex gap-2">
        {confirmingForward && offer.listingAvailableOn && (
          <ForwardCommitDialog
            availableOn={offer.listingAvailableOn}
            onConfirm={() => acceptCounter.mutate()}
            onCancel={() => setConfirmingForward(false)}
            isPending={acceptCounter.isPending}
          />
        )}
        <button
          type="button"
          onClick={() => (forwardDated ? setConfirmingForward(true) : acceptCounter.mutate())}
          disabled={acceptCounter.isPending}
          className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
        >
          {acceptCounter.isPending ? '…' : 'Accept counter'}
        </button>
        <button
          type="button"
          onClick={() => withdraw.mutate()}
          disabled={withdraw.isPending}
          className="rounded border border-zinc-300 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
        >
          {withdraw.isPending ? '…' : 'Decline'}
        </button>
      </div>
    );
  }

  if (offer.status === 'accepted' || offer.status === 'deposit_held') {
    return (
      <div className="flex flex-col items-start gap-2">
        {offer.status === 'accepted' && <DepositAction offer={offer} />}
        {offer.status === 'deposit_held' && <BalanceAction offer={offer} />}
        <AmendOfferAction offer={offer} />
      </div>
    );
  }

  if (offer.status !== 'pending') return null;

  return (
    <button
      type="button"
      onClick={() => withdraw.mutate()}
      disabled={withdraw.isPending}
      className="rounded border border-zinc-300 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
    >
      {withdraw.isPending ? '…' : 'Withdraw'}
    </button>
  );
}

function AmountCell({ offer }: { offer: Offer }) {
  if (offer.status === 'countered' && offer.counterAmountUsd != null) {
    return (
      <div className="flex flex-col">
        <span className="text-xs text-zinc-400 line-through">
          <UsdCell value={offer.amountUsd} />
        </span>
        <span className="text-sm font-medium text-blue-700">
          <UsdCell value={offer.counterAmountUsd} /> <span className="text-xs">(counter)</span>
        </span>
      </div>
    );
  }
  return <UsdCell value={offer.amountUsd} />;
}

// ---------------------------------------------------------------------------
// SIL-221: "Listing" cell drill-down link into /listings/$listingId. Gated on
// the server-computed listingViewable (leftJoin resolving under listing RLS,
// migration 606) — NOT on offer.status. This is deliberate: the seller always
// sees its own listing regardless of offer status; a buyer with a withdrawn/
// rejected offer on a still-active listing can still view it; and it won't
// silently rot when a new offer status is added later. Falls back to the raw
// UUID stub when displayTitle is null (still linked when listingViewable).
// ---------------------------------------------------------------------------
function ListingCell({ offer }: { offer: Offer }) {
  const label = offer.displayTitle ?? offer.listingId.slice(0, 8);
  const content = (
    <>
      <span className={offer.displayTitle ? 'text-sm font-medium text-zinc-900' : 'font-mono text-xs text-zinc-700'}>
        {label}
      </span>
      {offer.conditionBand && (
        <span className="block text-xs text-zinc-500">{offer.conditionBand}</span>
      )}
    </>
  );

  if (!offer.listingViewable) {
    return <span className="inline-block">{content}</span>;
  }

  return (
    <Link
      to="/listings/$listingId"
      params={{ listingId: offer.listingId }}
      aria-label={`${label}, ${offer.conditionBand ?? ''}`}
      className="inline-block hover:underline"
    >
      {content}
    </Link>
  );
}

// Per-row actor determination: a dual-profile org can be the BUYER on some
// offers and the SELLER on others in the same union list (RUF-536 dual
// profiles), so the row's actions are gated on the server-computed isOwnOffer
// (buyer org === viewer's customer_org) rather than the session's global
// orgType. Do NOT compare against orgId — that is the profile org, which
// differs from the customer_org that owns the offer.
function columns(): ColumnDef<Offer, unknown>[] {
  return [
    {
      accessorKey: 'listingId',
      header: 'Listing',
      cell: (ctx) => <ListingCell offer={ctx.row.original} />,
    },
    {
      id: 'amount',
      header: 'Offer',
      cell: (ctx) => <AmountCell offer={ctx.row.original} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (ctx) => (
        <OfferStatusBadge
          value={ctx.getValue<string>()}
          refusalReason={ctx.row.original.refusalReason}
          cancellationReason={ctx.row.original.cancellationReason}
        />
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Submitted',
      cell: (ctx) => (
        <span className="text-sm text-zinc-700">{formatDate(ctx.getValue<string>())}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (ctx) => {
        const offer = ctx.row.original;
        return offer.isOwnOffer ? (
          <BuyerOfferActions offer={offer} />
        ) : (
          <SellerOfferActions offer={offer} />
        );
      },
    },
  ];
}

function SellerOffersPage() {
  const { data, isFetching } = useQuery<OffersResponse>({
    queryKey: qk.offers(),
    queryFn: async () => {
      const res = await api.api.client.offers.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as OffersResponse;
    },
  });

  return (
    <div className="space-y-4">
      <PageHeader title="Offers" pinTo="/offers" />
      <p className="text-sm text-zinc-500">
        Review offers buyers have made on your listings — accept, reject, or counter with a
        different amount.
      </p>
      <DataTable
        data={data?.data ?? []}
        columns={columns()}
        emptyMessage={isFetching ? 'Loading…' : 'No offers yet.'}
      />
    </div>
  );
}

function OffersPage() {
  const { orgType } = useAuth();
  // SIL-1351: the buyer's offers live on the My Activity surface (spec
  // section 4.6), rendered here and at /transactions. The seller table,
  // which also carries the seller-side accept / reject / counter actions,
  // is unchanged.
  if (orgType === 'buyer') return <MyActivity pinTo="/offers" />;
  return <SellerOffersPage />;
}
